import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Heart, AlertCircle } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { savePalette } from '../config/savedPalettes';

export default function SavePaletteModal({ isOpen, onClose, palette }) {
  const { t } = useI18n();
  const { user, login } = useAuth();
  const [paletteName, setPaletteName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [error, setError] = useState('');

  // Reset state when modal closes (fixes bug where state persists after manual close)
  useEffect(() => {
    if (!isOpen) {
      setPaletteName('');
      setIsSaving(false);
      setIsSaved(false);
      setError('');
    }
  }, [isOpen]);

  // Lock background scroll while open, matching Load/Share modals
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
      return () => { document.documentElement.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!paletteName.trim()) {
      setError(t('savedPalettes.errors.nameRequired'));
      return;
    }

    if (paletteName.length > 50) {
      setError(t('savedPalettes.errors.nameTooLong'));
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const result = await savePalette(user.uid, palette, paletteName);

      if (result.success) {
        setIsSaved(true);
        // Close modal after 1.5 seconds to show "Saved" state
        setTimeout(() => {
          setPaletteName('');
          setIsSaved(false);
          onClose();
        }, 1500);
      } else {
        setError(result.error);
        setIsSaving(false);
      }
    } catch (err) {
      setError(err.message || t('savedPalettes.errors.saveFailed'));
      setIsSaving(false);
    }
  };

  const handleSignIn = async () => {
    setIsSaving(true);
    try {
      await login();
      // After login, modal will reopen with user authenticated
    } catch (err) {
      setError(t('savedPalettes.errors.loginFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  // If user is not logged in, show sign-in prompt
  if (!user) {
    return createPortal(
      <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeOnly">
        <div
          className="absolute inset-0 z-0"
          onClick={onClose}
        />
        <div className="relative z-10 bg-white/95 backdrop-blur-md rounded-2xl max-w-md w-full p-6 shadow-soft-xl border border-rose-pastel-100/50">
          {/* Header */}
          {/* <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-800 text-center w-full">
              {t('savedPalettes.signInPrompt.title')}
            </h2>
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-9 h-9  text-slate-600 hover:text-slate-800 flex items-center justify-center"
              aria-label={t("sharePaletteModal.closeAria")}
              disabled={isSaving}
            >
              ✕
            </button>
          </div> */}
          <div className="mb-4 lg:mb-6">
            <h3 className="text-lg lg:text-xl font-display font-semibold text-slate-800 mb-2">{t('savedPalettes.signInPrompt.title')}</h3>
            <div className="w-16 h-1 bg-gradient-to-r from-rose-pastel-400 to-lavender-400 rounded-full"></div>
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-9 h-9  text-slate-600 hover:text-slate-800 flex items-center justify-center"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="mb-6 text-center">
            {/* <p className="text-sm text-elegant mb-4">
              {t('savedPalettes.signInPrompt.message')}
            </p> */}
            <ul className="text-left text-slate-600 space-y-2 mb-6">
              <li>• {t('savedPalettes.signInPrompt.benefit1')}</li>
              <li>• {t('savedPalettes.signInPrompt.benefit2')}</li>
              <li>• {t('savedPalettes.signInPrompt.benefit3')}</li>
            </ul>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-xl bg-white/80 hover:bg-slate-50 border border-slate-200 text-slate-600 text-xs font-medium transition-all"
              disabled={isSaving}
            >
              {t('savedPalettes.buttons.cancel')}
            </button>
            <button
              onClick={handleSignIn}
              className="flex-1 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-400 to-teal-500 hover:from-emerald-500 hover:to-teal-600 text-white text-xs font-medium shadow-soft transition-all"
              disabled={isSaving}
            >
              {isSaving ? t('savedPalettes.buttons.signingIn') : t('savedPalettes.buttons.signIn')}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // User is logged in - show save form
  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeOnly">
      <div
        className="absolute inset-0 z-0"
        onClick={onClose}
      />
      <div className="relative z-10 bg-white/95 backdrop-blur-md rounded-2xl max-w-md w-full p-6 shadow-soft-xl border border-rose-pastel-100/50">
        {/* Header */}
        {/* <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-slate-800 text-center w-full">
            {t('savedPalettes.saveModal.title')}
          </h2>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9  text-slate-600 hover:text-slate-800 flex items-center justify-center"
            disabled={isSaving}
          >
            ✕
          </button>
        </div> */}
        <div className="mb-4 lg:mb-6">
            <h3 className="text-lg lg:text-xl font-display font-semibold text-slate-800 mb-2">{t('savedPalettes.saveModal.title')}</h3>
            <div className="w-16 h-1 bg-gradient-to-r from-rose-pastel-400 to-lavender-400 rounded-full"></div>
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-9 h-9  text-slate-600 hover:text-slate-800 flex items-center justify-center"
            >
              ✕
            </button>
          </div>

        {/* Content */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {t('savedPalettes.saveModal.nameLabel')}
          </label>
          <input
            type="text"
            value={paletteName}
            onChange={(e) => setPaletteName(e.target.value)}
            placeholder={t('savedPalettes.saveModal.namePlaceholder')}
            maxLength={50}
            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 focus:border-transparent bg-white/80 backdrop-blur-sm transition-all"
            disabled={isSaving}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isSaving) {
                handleSave();
              }
            }}
          />
          <p className="text-xs text-slate-500 mt-1">
            {paletteName.length}/50 {t('savedPalettes.saveModal.characters')}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Palette Info */}
        <div className="mb-6">
          <p className="text-xs text-slate-500">
            {t('savedPalettes.saveModal.colorCount')}: <span className="font-semibold text-slate-700">{palette.length}</span>
          </p>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 rounded-xl bg-white/80 hover:bg-slate-50 border border-slate-200 text-slate-600 text-xs font-medium transition-all"
            disabled={isSaving}
          >
            {t('savedPalettes.buttons.cancel')}
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 rounded-xl bg-gradient-to-r from-rose-pastel-300 to-pink-300 hover:from-rose-pastel-400 hover:to-pink-400 text-white text-xs font-medium shadow-soft transition-all inline-flex items-center justify-center gap-2"
            disabled={isSaving || !paletteName.trim()}
          >
            <Heart size={14} fill={isSaved ? "currentColor" : "none"} />
            {isSaved ? 'Saved' : (isSaving ? t('savedPalettes.buttons.saving') : t('savedPalettes.buttons.save'))}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
